import {
  TDDActivateRequestData,
  TDDUserTokenData,
  TDDGetFeatureArtistsResponse,
  TDDGetGenresResponse,
  TDDGetLanguagesResponse,
  TDDGetMainArtistsResponse,
  TDDGetRecordLabelsResponse,
  TDDGetStoresResponse,
  TDDGetUserDetailsResponse,
  TDDSuccessResponse,
  TDDUpdateCountryRequestData,
  TDDUpdateEmailRequestData,
  TDDUpdateFirstNameRequestData,
  TDDUpdateLastNameRequestData,
  TDDUpdatePasswordRequestData,
  TDDGetCountriesResponse,
  TDDGetReleaseByIDResponse,
  TUserIdType,
  EDDCatalogueType,
  TDDGetCatalogueByTypeResponse,
  TDDGetTracksByReleaseResponse,
  TDDGetTracksResponse,
  TDDGetComposerTypesResponse,
  TDDGetSpotifyUsersResponse,
  TDDAddFeatureArtistResponse,
  TDDAddMainArtistResponse,
  TDDComposerTypeCode,
  TDDComposeTypeName,
  TDDAddComposerResponse,
  TDDAddRecordLabelResponse,
  TDDGetAppleUsersResponse,
  TDDStartReleaseResponse,
  TDDSetReleaseDateRequestData,
  EDDReleaseType,
  TDDSetReleaseDateRequestDataForNew,
  TDDSetReleaseDateRequestDataForOld,
  TDDSuccessObjectResponse,
  TDDSetReleaseGenreResponse,
  TDDSetReleaseCountriesResponse,
  TDDSetReleaseCopyrightResponse,
  TDDSetReleaseLanguageResponse,
  TDDSetReleaseStoresResponse,
  TDDSetReleaseRecordLabelResponse,
  TDDSetReleaseArtworkResponse,
  TDDReleaseUploadTrackRequestData,
  TDDReleaseUploadTrackResponse,
  TDDSubmitReleaseResponse,
  TDDDiscardCatalogueResponse,
  TDDDeleteTrackResponse,
  TDDGetComposersByTypeResponse,
} from './distro-direct-types';

/**
 * 1 - API ID= Client-ID
 * 2 - API KEY= Client-Secret
 * 3 - COMPANY ID = Company ID
 * 4 - SSO AUTH TOKEN = auth-token
 */
const API_ID = '7p59sak47elfke65tb93t5psb7';
const API_KEY = '13g4g17fgjag1f775tmbhjtjeigj0fdncovn1q6pnnic40kqfrh7';
const COMPANY_ID = 423;
const SSO_AUTH_TOKEN = '3miK4wCZyyK81Lu3f';

export class DDError extends Error {
  ddResponse?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let DistroAPI: { [key: string]: any };

class DistroDirect {
  #userToken: string | null = null;

  set userToken(val: string | null) {
    this.#userToken = val ? val : null;
  }

  get userToken() {
    if (!this.#userToken) {
      throw new Error('Missing user token');
    }

    return this.#userToken;
  }

  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async callApi<T>(name: string, ...args: any[]): Promise<T> {
    if (!DistroAPI) {
      throw new Error(`The DistroAPI not available`);
    }

    if (!DistroAPI[name]) {
      throw new Error(`DistroAPI method ${name} not found`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    let res = await DistroAPI[name](...args);

    if (typeof res === 'string' && res.trim().startsWith('{')) {
      try {
        res = JSON.parse(res);
      } catch (error) {
        if (error instanceof Error) {
          console.warn(
            `Failed to parse response from API method ${name}, we considered it to be a JSON but it failed to parse with error: ${error.message}`,
          );
          console.warn(`Response: ${res}`);
        }
      }
    }

    if (
      res?.status &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      res.status.toLowerCase().trim() !== 'success' &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      !res.status.startsWith('https://')
    ) {
      const err = new DDError(`API method ${name} failed with status: ${res.status}`);
      err.ddResponse = res;
      throw err;
    }

    return res as T;
  }
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

  /**
   * @method activate
   *
   * It allows you to log in as a user already defined in the Distro Direct
   * database with assigned a `user_unique_id`.
   *
   * If a specific user is defined in the Distro Direct database but does not
   * have a `user_unique_id` assigned, the `user_unique_id` provided in the
   * request will be assigned to the user permanently without the possibility
   * of changing it, and we will be logged in as this user.
   *
   * If, during the request, we provide data of a user that does not yet exist
   * in the Distro Direct database, a new user will be created and we will be
   * logged in as this user.
   */
  activate(data: TDDActivateRequestData): Promise<TDDUserTokenData> {
    const { user_first_name, user_last_name, country, user_email, user_password, user_unique_id } =
      data;

    return this.callApi<TDDUserTokenData>(
      'activate',
      API_ID,
      API_KEY,
      SSO_AUTH_TOKEN,
      COMPANY_ID,
      user_first_name,
      user_last_name,
      country,
      user_email,
      user_password,
      user_unique_id,
    ).then((res) => {
      if (!res) {
        throw new Error('Failed to activate user');
      }

      return res;
    });
  }

  activateAndGetUser(data: TDDActivateRequestData): Promise<DistroDirectUser> {
    return this.activate(data).then((res) => {
      const distroDirect = new DistroDirect();
      distroDirect.userToken = res.access_token;
      return new DistroDirectUser(distroDirect, data.user_unique_id);
    });
  }

  lastNameUpdate(data: TDDUpdateLastNameRequestData): Promise<TDDSuccessResponse> {
    const { new_lastname, firstname, country, email, password, user_unique_id } = data;

    return this.callApi<TDDSuccessResponse>(
      'lastnameUpdate',
      new_lastname,
      firstname,
      country,
      email,
      password,
      COMPANY_ID,
      user_unique_id,
      SSO_AUTH_TOKEN,
    );
  }

  firstNameUpdate(data: TDDUpdateFirstNameRequestData): Promise<TDDSuccessResponse> {
    const { new_firstname, lastname, country, email, password, user_unique_id } = data;

    return this.callApi<TDDSuccessResponse>(
      'firstnameUpdate',
      new_firstname,
      lastname,
      country,
      email,
      password,
      COMPANY_ID,
      user_unique_id,
      SSO_AUTH_TOKEN,
    );
  }

  countryUpdate(data: TDDUpdateCountryRequestData) {
    const { new_country, firstname, lastname, email, password, user_unique_id } = data;

    return this.callApi<TDDSuccessResponse>(
      'countryUpdate',
      new_country,
      firstname,
      lastname,
      email,
      password,
      COMPANY_ID,
      user_unique_id,
      SSO_AUTH_TOKEN,
    );
  }

  emailUpdate(data: TDDUpdateEmailRequestData) {
    const { new_email, firstname, lastname, country, password, user_unique_id } = data;

    return this.callApi<TDDSuccessResponse>(
      'emailUpdate',
      new_email,
      firstname,
      lastname,
      country,
      password,
      COMPANY_ID,
      user_unique_id,
      SSO_AUTH_TOKEN,
    );
  }

  passwordUpdate(data: TDDUpdatePasswordRequestData) {
    const { new_password, firstname, lastname, country, email, user_unique_id } = data;

    return this.callApi<TDDSuccessResponse>(
      'passwordUpdate',
      new_password,
      firstname,
      lastname,
      country,
      email,
      COMPANY_ID,
      user_unique_id,
      SSO_AUTH_TOKEN,
    );
  }

  /*--------------------------------------------------------------------------*/
  /* Information retrieval
  /*--------------------------------------------------------------------------*/

  getUserDetails(user_unique_id: TUserIdType) {
    return this.callApi<TDDGetUserDetailsResponse>(
      'getUserDetails',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      API_ID,
    );
  }

  getMainArtists(user_unique_id: TUserIdType) {
    return this.callApi<TDDGetMainArtistsResponse>(
      'getMainArtists',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      API_ID,
    );
  }

  getFeatureArtists(user_unique_id: TUserIdType) {
    return this.callApi<TDDGetFeatureArtistsResponse>(
      'getFeatureArtists',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      API_ID,
    );
  }

  getLanguages() {
    return this.callApi<TDDGetLanguagesResponse>(
      'getLanguages',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      API_ID,
    );
  }

  getGenres() {
    return this.callApi<TDDGetGenresResponse>(
      'getGenres',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      API_ID,
    );
  }

  getRecordLabels(user_unique_id: TUserIdType) {
    return this.callApi<TDDGetRecordLabelsResponse>(
      'getRecordLabels',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      API_ID,
    );
  }

  getStores() {
    return this.callApi<TDDGetStoresResponse>(
      'getStores',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      API_ID,
    );
  }

  getCountries() {
    return this.callApi<TDDGetCountriesResponse>(
      'getCountries',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      API_ID,
    );
  }

  getReleaseByID(user_unique_id: TUserIdType, release_id: number) {
    return this.callApi<TDDGetReleaseByIDResponse>(
      'getReleaseByID',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      release_id,
      API_ID,
    );
  }

  getCatalogueByType(user_unique_id: TUserIdType, type: EDDCatalogueType) {
    return this.callApi<TDDGetCatalogueByTypeResponse>(
      'getCatalogueByType',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      type,
      API_ID,
    );
  }

  /**
   *
   * @param release_id Could be provided as empty string but then the `release_UPC` is required
   */
  getTracksByRelease(user_unique_id: TUserIdType, release_id: number | '', release_UPC?: number) {
    return this.callApi<TDDGetTracksByReleaseResponse>(
      'getTracksByRelease',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      release_id,
      release_UPC,
      API_ID,
    );
  }

  /**
   *
   * @param track_id Could be provided as empty string but then the `release_ISRC` is required
   */
  getTracks(user_unique_id: TUserIdType, track_id: number | '', release_ISRC?: string) {
    return this.callApi<TDDGetTracksResponse>(
      'getTracks',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      track_id,
      release_ISRC,
      API_ID,
    );
  }

  getComposerTypes() {
    return this.callApi<TDDGetComposerTypesResponse>(
      'getComposerTypes',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      API_ID,
    );
  }

  async getSpotifyUsers(user_unique_id: TUserIdType, artist_name: string) {
    return await this.callApi<TDDGetSpotifyUsersResponse>(
      'getSpotifyUsers',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      artist_name,
      API_ID,
    );
  }

  async getAppleUsers(user_unique_id: TUserIdType, artist_name: string) {
    return await this.callApi<TDDGetAppleUsersResponse>(
      'getAppleUsers',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      artist_name,
      API_ID,
    );
  }

  async getComposersByType(user_unique_id: TUserIdType, composer_type: TDDComposerTypeCode) {
    return await this.callApi<TDDGetComposersByTypeResponse>(
      'getComposersByType',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      composer_type,
      API_ID,
    );
  }

  /*--------------------------------------------------------------------------*/
  /* Information insert
  /*--------------------------------------------------------------------------*/

  addMainArtist(user_unique_id: TUserIdType, main_artist_name: string) {
    if (main_artist_name.includes(',')) {
      throw new Error('Main artist name cannot contain comma');
    }

    return this.callApi<TDDAddMainArtistResponse>(
      'addMainArtist',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      main_artist_name,
      API_ID,
    );
  }

  addFeatureArtist(user_unique_id: TUserIdType, feature_artist_name: string) {
    if (feature_artist_name.includes(',')) {
      throw new Error('Feature artist name cannot contain comma');
    }

    return this.callApi<TDDAddFeatureArtistResponse>(
      'addFeatureArtist',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      feature_artist_name,
      API_ID,
    );
  }

  /**
   * @param composer_type composer code from `getComposerTypes`
   */
  addComposer(
    user_unique_id: TUserIdType,
    composer_name: TDDComposeTypeName,
    composer_type: TDDComposerTypeCode,
  ) {
    return this.callApi<TDDAddComposerResponse>(
      'addComposer',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      composer_name,
      composer_type,
      API_ID,
    );
  }

  addRecordLabel(user_unique_id: TUserIdType, record_label_name: string) {
    return this.callApi<TDDAddRecordLabelResponse>(
      'addRecordLabel',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      record_label_name,
      API_ID,
    );
  }

  /**
   * @param apple_id Apple ID of the main artist, obtained from `getAppleUsers` (`id` field)
   */
  setAppleID(user_unique_id: TUserIdType, artist_name: string, apple_id: number) {
    return this.callApi<TDDSuccessResponse>(
      'setAppleID',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      artist_name,
      apple_id,
      API_ID,
    );
  }

  /**
   * @param spotify_uri Spotify URI of the main artist, obtained from `getSpotifyUsers` (`id` field)
   */
  setSpotifyURI(user_unique_id: TUserIdType, artist_name: string, spotify_uri: string) {
    return this.callApi<TDDSuccessResponse>(
      'setSpotifyURI',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      artist_name,
      spotify_uri,
      API_ID,
    );
  }

  /*--------------------------------------------------------------------------*/
  /* Release building
  /*--------------------------------------------------------------------------*/

  /**
   * @param main_artists Array of main artists or `Various Artists`. Array items must be first added using addMainArtist()
   */
  startRelease(
    user_unique_id: TUserIdType,
    release_title: string,
    main_artists: string[] | 'Various Artists',
  ) {
    const main_artists_normalized: string = Array.isArray(main_artists)
      ? main_artists.join(',')
      : main_artists;

    return this.callApi<TDDStartReleaseResponse>(
      'startRelease',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      main_artists_normalized,
      release_title,
      API_ID,
    );
  }

  getReleaseService(user_unique_id: TUserIdType, release_id: string) {
    return new DistroDirectRelease(this, user_unique_id, release_id);
  }

  /*--------------------------------------------------------------------------*/
  /* Analytics
  /*--------------------------------------------------------------------------*/
  getAnalytics(user_unique_id: TUserIdType) {
    return this.callApi<TDDStartReleaseResponse>(
      'getAnalytics',
      SSO_AUTH_TOKEN,
      this.userToken,
      COMPANY_ID,
      user_unique_id,
      API_ID,
    );
  }
}

export class DistroDirectUser {
  #distroDirect: DistroDirect;
  #userUniqueId: TUserIdType;

  constructor(distroDirect: DistroDirect, userUniqueId: TUserIdType) {
    this.#distroDirect = distroDirect;
    this.#userUniqueId = userUniqueId;
  }

  getUserDetails() {
    return this.#distroDirect.getUserDetails(this.#userUniqueId);
  }

  getMainArtists() {
    return this.#distroDirect.getMainArtists(this.#userUniqueId);
  }

  getFeatureArtists() {
    return this.#distroDirect.getFeatureArtists(this.#userUniqueId);
  }

  getLanguages() {
    return this.#distroDirect.getLanguages();
  }

  getRecordLabels() {
    return this.#distroDirect.getRecordLabels(this.#userUniqueId);
  }

  getStores() {
    return this.#distroDirect.getStores();
  }

  getCountries() {
    return this.#distroDirect.getCountries();
  }

  getReleaseByID(release_id: number) {
    return this.#distroDirect.getReleaseByID(this.#userUniqueId, release_id);
  }

  getCatalogueByType(type: EDDCatalogueType) {
    return this.#distroDirect.getCatalogueByType(this.#userUniqueId, type);
  }

  getTracksByRelease(release_id: number | '', release_UPC?: number) {
    return this.#distroDirect.getTracksByRelease(this.#userUniqueId, release_id, release_UPC);
  }

  getTracks(track_id: number | '', release_ISRC?: string) {
    return this.#distroDirect.getTracks(this.#userUniqueId, track_id, release_ISRC);
  }

  getComposerTypes() {
    return this.#distroDirect.getComposerTypes();
  }

  getSpotifyUsers(artist_name: string) {
    return this.#distroDirect.getSpotifyUsers(this.#userUniqueId, artist_name);
  }

  getAppleUsers(artist_name: string) {
    return this.#distroDirect.getAppleUsers(this.#userUniqueId, artist_name);
  }

  getComposersByType(composer_type: TDDComposerTypeCode) {
    return this.#distroDirect.getComposersByType(this.#userUniqueId, composer_type);
  }

  addMainArtist(main_artist_name: string) {
    return this.#distroDirect.addMainArtist(this.#userUniqueId, main_artist_name);
  }

  addFeatureArtist(feature_artist_name: string) {
    return this.#distroDirect.addFeatureArtist(this.#userUniqueId, feature_artist_name);
  }

  addComposer(composer_name: TDDComposeTypeName, composer_type: TDDComposerTypeCode) {
    return this.#distroDirect.addComposer(this.#userUniqueId, composer_name, composer_type);
  }

  addRecordLabel(record_label_name: string) {
    return this.#distroDirect.addRecordLabel(this.#userUniqueId, record_label_name);
  }

  setAppleID(artist_name: string, apple_id: number | string) {
    return this.#distroDirect.setAppleID(
      this.#userUniqueId,
      artist_name,
      typeof apple_id === 'string' ? parseInt(apple_id, 10) : apple_id,
    );
  }

  setSpotifyURI(artist_name: string, spotify_uri: string) {
    return this.#distroDirect.setSpotifyURI(this.#userUniqueId, artist_name, spotify_uri);
  }

  setSpotifyID(artist_name: string, spotify_id: string) {
    return this.setSpotifyURI(artist_name, `spotify:artist:${spotify_id}`);
  }

  startRelease(release_title: string, main_artists: string[] | 'Various Artists') {
    return this.#distroDirect.startRelease(this.#userUniqueId, release_title, main_artists);
  }

  getReleaseService(release_id: string) {
    return this.#distroDirect.getReleaseService(this.#userUniqueId, release_id);
  }

  getAnalytics() {
    return this.#distroDirect.getAnalytics(this.#userUniqueId);
  }
}

export class DistroDirectRelease {
  #distroDirect: DistroDirect;
  #userUniqueId: TUserIdType;
  #releaseId: string;

  constructor(distroDirect: DistroDirect, userUniqueId: TUserIdType, releaseId: string) {
    this.#distroDirect = distroDirect;
    this.#userUniqueId = userUniqueId;
    this.#releaseId = releaseId;
  }

  setReleaseDate(data: TDDSetReleaseDateRequestData) {
    const { release_type, release_date, original_release_date, preorder_date } = data;

    return this.#distroDirect.callApi<TDDSuccessObjectResponse>(
      'setReleaseDate',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      release_type,
      release_date,
      original_release_date,
      preorder_date,
      API_ID,
    );
  }

  /**
   * @method setReleaseDateForNewRelease
   * Allows to set the release date for the New Album
   */
  setReleaseDateForNewRelease(data: TDDSetReleaseDateRequestDataForNew) {
    const inputData: TDDSetReleaseDateRequestData = {
      release_type: EDDReleaseType.new,
      original_release_date: '',
      ...data,
    };

    return this.setReleaseDate(inputData);
  }

  /**
   * @method setReleaseDateForOldRelease
   * Allows to set the date for Re-release of existing album or single
   */
  setReleaseDateForOldRelease(data: TDDSetReleaseDateRequestDataForOld) {
    const inputData: TDDSetReleaseDateRequestData = {
      release_type: EDDReleaseType.old,
      release_date: '',
      preorder_date: '',
      ...data,
    };

    return this.setReleaseDate(inputData);
  }

  /**
   * @param copyrightYear Warning! year is not visible in the UI, UI shows year based on release date
   * @param copyrightHolder pass empty string to default to main artist
   */
  setCopyright(copyrightYear: number, copyrightHolder: string) {
    const def = copyrightHolder ? 'false' : 'true';

    // TODO: setting default does not appear to be working?

    return this.#distroDirect.callApi<TDDSetReleaseCopyrightResponse>(
      'setCopyright',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      copyrightYear,
      def,
      copyrightHolder,
      API_ID,
    );
  }

  setCountries(countries: string[] | 'ALL') {
    const countriesNormalized: string = Array.isArray(countries) ? countries.join(',') : '';

    return this.#distroDirect.callApi<TDDSetReleaseCountriesResponse>(
      'setCountries',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      countriesNormalized,
      String(countries === 'ALL'),
      API_ID,
    );
  }

  /**
   * @param genre Genre code from `getGenres`
   */
  setGenre(type: 'primary' | 'secondary', genre: string) {
    return this.#distroDirect.callApi<TDDSetReleaseGenreResponse>(
      `set${type === 'primary' ? 'Primary' : 'Secondary'}Genre`,
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      genre,
      API_ID,
    );
  }

  /**
   * @param language Language code from `getLanguages`
   */
  setLanguage(language: string) {
    return this.#distroDirect.callApi<TDDSetReleaseLanguageResponse>(
      'setLanguage',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      language,
      API_ID,
    );
  }

  /**
   * @param stores Store codes from `getStores`
   */
  setStores(stores: string[]) {
    const storesNormalized: string = stores.join(',');

    return this.#distroDirect.callApi<TDDSetReleaseStoresResponse>(
      'setStores',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      storesNormalized,
      API_ID,
    );
  }

  /**
   *
   * @param recordLabel  Record label name from `getRecordLabels`
   */
  setRecordLabel(recordLabel: string) {
    return this.#distroDirect.callApi<TDDSetReleaseRecordLabelResponse>(
      'setRecordLabel',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      recordLabel,
      API_ID,
    );
  }

  setArtwork(artwork: Blob) {
    const formData = new FormData();
    formData.append('files[]', artwork);

    return this.#distroDirect.callApi<TDDSetReleaseArtworkResponse>(
      'setReleaseArtwork',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      formData,
      API_ID,
    );
  }

  uploadTrack(data: TDDReleaseUploadTrackRequestData) {
    const formData = new FormData();

    if (data.file) {
      formData.append('files[]', data.file);
    }

    if (!data.mainArtists) data.mainArtists = []; // TODO: required?
    const mainArtistsNormalized = !Array.isArray(data.mainArtists)
      ? [data.mainArtists].filter(Boolean)
      : data.mainArtists;

    if (mainArtistsNormalized.some((artist) => artist.includes(','))) {
      throw new Error('Main artist name cannot contain comma');
    }

    if (data.featureArtists) {
      if (data.featureArtists.some((artist) => artist.includes(','))) {
        throw new Error('Feature artist name cannot contain comma');
      }
    }

    const separators = { code: '[code]', name: '[name]', separator: '[seperator]' };
    const separatorsValues = Object.values(separators);
    const composersNormalized = (data.composers || [])
      .map((composer) => {
        separatorsValues.forEach((separator) => {
          if (composer.code.includes(separator) || composer.name.includes(separator)) {
            throw new Error(`Composer code or name cannot contain "${separator}"`);
          }
        });

        return `${separators.code}${composer.code}${separators.name}${composer.name}`;
      })
      .join(separators.separator);

    return this.#distroDirect.callApi<TDDReleaseUploadTrackResponse>(
      'uploadTrack',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      data.name,
      data.versionTitle || '',
      mainArtistsNormalized.join(','),
      (data.featureArtists || []).join(','),
      data.authority,
      composersNormalized,
      data.isrc || '',
      data.isExplicit ? '1' : '0',
      data.tikTokStartTime || '',
      data.language || '',
      formData,
      API_ID,
    );
  }

  /**
   * Method allows to submit your release for publishing, which will move it to
   * `pending` status for review by Distro Direct content team prior to distribution.
   */
  submitRelease() {
    return this.#distroDirect.callApi<TDDSubmitReleaseResponse>(
      'submitRelease',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      API_ID,
    );
  }

  /**
   * Method allows to delete release from Distro Direct.
   * A release can only be deleted when it is in `incomplete` status.
   */
  discardCatalogue() {
    return this.#distroDirect.callApi<TDDDiscardCatalogueResponse>(
      'discardCatalogue',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      this.#releaseId,
      API_ID,
    );
  }

  deleteTrack(trackId: string) {
    return this.#distroDirect.callApi<TDDDeleteTrackResponse>(
      'deleteTrack',
      SSO_AUTH_TOKEN,
      this.#distroDirect.userToken,
      COMPANY_ID,
      this.#userUniqueId,
      trackId,
      API_ID,
    );
  }

  getTracks() {
    return this.#distroDirect.getTracksByRelease(this.#userUniqueId, parseInt(this.#releaseId));
  }
}

export const distroDirectService = new DistroDirect();
